import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import { ReportCategory } from '~hooks/useOmniReports/models';
import { useOmniReports } from '~hooks/useOmniReports/useOmniReports';
import { InfoIcon } from '~icons/InfoIcon';

interface ReportCategoryDialogProps {
  companyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (reportCategory: ReportCategory) => void;
  onDelete: (reportCategory: ReportCategory) => void;
  defaultReportCategory: ReportCategory | null;
}

const pageFormValidationSchema = yup.object().shape({
  name: yup
    .string()
    .required(t('form_validation_errors.required_simple'))
    .min(1, t('form_validation_errors.required_simple')),
  system: yup.boolean().notRequired(),
});

type PageFormDTO = yup.InferType<typeof pageFormValidationSchema>;

const ReportCategoryDialog = ({
  companyId,
  open,
  onClose,
  onSuccess,
  onDelete,
  defaultReportCategory,
}: ReportCategoryDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { createReportCategory, updateReportCategory, deleteReportCategory } =
    useOmniReports();

  const isEdit = defaultReportCategory !== null;
  // System updates are only allowed for TREAD company and on Create
  const canAddSystem = !isEdit && companyId === TREAD_UUID;

  const { control, handleSubmit, reset, setError } = useForm<PageFormDTO>({
    resolver: yupResolver(pageFormValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: defaultReportCategory?.name ?? '',
      system: defaultReportCategory?.system ?? false,
    },
  });

  useEffect(() => {
    reset({
      name: defaultReportCategory?.name ?? '',
      system: defaultReportCategory?.system ?? false,
    });
  }, [defaultReportCategory, reset]);

  const handleClose = useCallback(() => {
    setIsDeleteDialogOpen(false);
    onClose();
    reset();
  }, [onClose, reset]);

  const onSubmit = useCallback(() => {
    handleSubmit(async (data) => {
      setIsLoading(true);
      try {
        if (isEdit) {
          const reportCategory = await updateReportCategory(
            companyId,
            defaultReportCategory.id,
            data.name,
          );
          if (reportCategory) {
            onSuccess(reportCategory);
          }
        } else {
          const reportCategory = await createReportCategory(
            companyId,
            data.name,
            data.system ?? false,
          );
          if (reportCategory) {
            onSuccess(reportCategory);
          }
        }
        handleClose();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          const apiErrors = (error.response?.data?.error?.errors ??
            []) as ModelError_Item[];
          const fieldMap: Record<string, keyof PageFormDTO> = {
            name: 'name',
          };

          apiErrors.forEach(({ field, message }) => {
            const formField = fieldMap[field];

            if (formField) {
              setError(formField, { type: 'manual', message });
            }
          });

          return;
        }

        throw error;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [companyId, handleClose, onSuccess, setError, isEdit, defaultReportCategory?.id]);

  const handleDelete = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (defaultReportCategory) {
      setIsLoading(true);
      try {
        await deleteReportCategory(companyId, defaultReportCategory.id);
        onDelete(defaultReportCategory);
        handleClose();
      } finally {
        setIsLoading(false);
        setIsDeleteDialogOpen(false);
      }
    }
  }, [companyId, defaultReportCategory, deleteReportCategory, onDelete, handleClose]);

  return (
    <>
      <Dialog isOpen={open} onClose={handleClose}>
        <DialogHeader onClose={handleClose}>
          {isEdit
            ? t('administration.report_settings.edit_page')
            : t('administration.report_settings.create_page')}
        </DialogHeader>

        <DialogContent>
          <TextInput
            control={control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />

          {canAddSystem && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CheckboxInput
                control={control}
                label={t('common.system')}
                name="system"
                sx={{ mr: -2, width: 'fit-content' }}
              />

              <Tooltip
                arrow
                placement="right"
                slotProps={{
                  popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
                  },
                }}
                title={t('administration.report_settings.system_tooltip')}
              >
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {isEdit && (
            <SmallLoadingButton
              color="error"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleDelete}
              sx={{ mr: 'auto' }}
            >
              {t('actions.delete')}
            </SmallLoadingButton>
          )}

          <SmallButton variant="outlined" color="secondary" onClick={handleClose}>
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton loading={isLoading} onClick={onSubmit}>
            {isEdit ? t('actions.save') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isBusy={isLoading}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('administration.report_settings.delete_page_title')}
      >
        <Typography>
          {t('administration.report_settings.delete_page_description')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
};

export default ReportCategoryDialog;
